import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIdentity } from '../hooks/useIdentity';
import { storePairs, getLocalPairWords } from '../utils/pairing';
import { clearLocalIdentity } from '../utils/identity';
import { clearPairingConfirm } from '../services/pairingConfirm';
import {
  exportUserWalletData,
  downloadExportFile,
} from '../utils/exportImport';
import { usePairingWordsContext } from '../contexts/PairingWordsContext';

function modalOverlayStyle(): CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  };
}

function modalPanelStyle(): CSSProperties {
  return {
    background: 'var(--color-background, #fff)',
    color: 'var(--color-text)',
    padding: '1.5rem',
    borderRadius: '8px',
    maxWidth: '320px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  };
}

export function GlobalActionBar(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { identity, refreshIdentity } = useIdentity();
  const ctx = usePairingWordsContext();
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [exportConfirmError, setExportConfirmError] = useState<string | null>(null);
  const [showMots, setShowMots] = useState(false);

  // Detect iframe context
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  // Read URL parameters to control button visibility
  const showDeleteParam = searchParams.get('showDelete') === 'true';
  const showWordsParam = searchParams.get('showWords') === 'true';

  // Display logic: show by default (normal usage), hide in iframe unless forced by URL params
  const shouldShowDelete = !isInIframe || showDeleteParam;
  const shouldShowWords = !isInIframe || showWordsParam;

  const performDelete = async (): Promise<void> => {
    clearLocalIdentity();
    storePairs([]);
    await clearPairingConfirm();
    setShowExportConfirm(false);
    setExportConfirmError(null);
    refreshIdentity();
    navigate('/');
  };

  const handleExportThenDelete = async (): Promise<void> => {
    setExportConfirmError(null);
    try {
      const json = await exportUserWalletData();
      downloadExportFile(json);
      await performDelete();
    } catch (err) {
      setExportConfirmError(err instanceof Error ? err.message : 'Erreur export');
    }
  };

  const handleNoExportDelete = (): void => {
    void performDelete();
  };

  const mots =
    (ctx?.offerWords !== null && ctx?.offerWords !== undefined && ctx.offerWords.length > 0
      ? ctx.offerWords
      : null) ?? getLocalPairWords();
  const hasMots = mots !== null && mots.length > 0;

  return (
    <>
      <nav
        role="navigation"
        aria-label="Actions globales"
        style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          padding: '0.5rem',
          borderBottom: `1px solid var(--color-border)`,
          marginBottom: '0.5rem',
        }}
      >
        {identity !== null && shouldShowDelete && (
          <button
            type="button"
            onClick={() => {
              setExportConfirmError(null);
              setShowExportConfirm(true);
            }}
            title="Supprimer identité et pairs. Proposition d'export avant suppression."
            aria-label="Supprimer identité et pairs"
          >
            Supprimer
          </button>
        )}
        {shouldShowWords && (
          <button
            type="button"
            onClick={() => setShowMots(true)}
            title="Afficher les 17 mots (représentation clé publique, format type BIP32)"
            aria-label="Afficher les mots de ma clé publique"
          >
            Afficher les mots de ma clé publique
          </button>
        )}
      </nav>

      {showExportConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-confirm-title"
          style={modalOverlayStyle()}
        >
          <div style={modalPanelStyle()}>
            <h2 id="export-confirm-title">
              Voulez-vous exporter vos données avant de supprimer ?
            </h2>
            {exportConfirmError !== null && (
              <p role="alert" style={{ color: 'var(--color-error)' }}>
                {exportConfirmError}
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => void handleExportThenDelete()}
              >
                Oui
              </button>
              <button type="button" onClick={handleNoExportDelete}>
                Non
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowExportConfirm(false);
                  setExportConfirmError(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {showMots && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mots-modal-title"
          style={modalOverlayStyle()}
        >
          <div style={modalPanelStyle()}>
            <h2 id="mots-modal-title">Mots de ma clé publique</h2>
            {hasMots ? (
              <p
                aria-label="Mots clé publique"
                style={{
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  wordBreak: 'break-word',
                  margin: '1rem 0',
                }}
              >
                {mots.map((mot, i) => `${i + 1}. ${mot}`).join('  ')}
              </p>
            ) : (
              <p style={{ margin: '1rem 0' }}>
                Aucune paire locale. Configurez le pairing pour afficher les mots.
              </p>
            )}
            <button type="button" onClick={() => setShowMots(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </>
  );
}
